import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors + preview Zoho-write-block errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (error.response?.status === 403 && (error.response?.data?.detail || '').includes('Preview environment')) {
      import('sonner').then(({ toast }) => toast.error(error.response.data.detail));
    }
    return Promise.reject(error);
  }
);

// Live fix (25 Feb 2026): a 502 from a BrokerBin outage may arrive with its JSON body
// replaced by Cloudflare's own error page in Production — the real detail text is
// duplicated into the X-BrokerBin-Detail response header, so fall back to it here.
// `detail` can be a non-string (e.g. FastAPI's 422 validation error array) — only use
// it when it's a plain string, otherwise fall through to the header/fallback.
export const getErrorDetail = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return error.response?.headers?.['x-brokerbin-detail'] || fallback;
};

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// User APIs
export const userAPI = {
  list: (params = {}) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  updateRoles: (userId, data) => api.put(`/users/${userId}/roles`, data),
  bulkUpdateRole: (data) => api.put('/users/bulk-update-role', data),
  resetPassword: (userId, data) => api.post(`/users/${userId}/reset-password`, data),
  unlockLogin: (userId) => api.post(`/users/${userId}/unlock-login`),
  changePassword: (data) => api.post('/auth/change-password', data),
  toggleActive: (userId) => api.put(`/users/${userId}/toggle-active`),
  delete: (userId) => api.delete(`/users/${userId}`),
  hierarchy: () => api.get('/users/hierarchy'),
  updateHierarchy: (userId, data) => api.put(`/users/${userId}/hierarchy`, data),
  productHierarchy: () => api.get('/users/product-hierarchy'),
  assignProductSkus: (data) => api.post('/users/product-hierarchy/assign-skus', data),
  syncHierarchyFromCRM: () => api.post('/users/sync-hierarchy-from-crm'),
  hierarchyChanges: (params = {}) => api.get('/users/hierarchy-changes', { params }),
  hierarchyGaps: () => api.get('/users/hierarchy-gaps'),
  setManualManager: (userId, data) => api.put(`/users/${userId}/manual-manager`, data),
  search: (q) => api.get('/users/search', { params: { q } }),
  setStaffNumber: (userId, data) => api.put(`/users/${userId}/staff-number`, data),
  filterOptions: () => api.get('/users/filter-options'),
};

// Team rollup APIs (managers)
export const teamAPI = {
  summary: (params = {}) => api.get('/team/summary', { params }),
  member: (userId, params = {}) => api.get(`/team/member/${userId}`, { params }),
};

// Hierarchy drift APIs (admin)
export const driftAPI = {
  list: (params = {}) => api.get('/hierarchy-drift', { params }),
  detect: () => api.post('/hierarchy-drift/detect'),
  apply: (id) => api.post(`/hierarchy-drift/${id}/apply`),
  dismiss: (id) => api.post(`/hierarchy-drift/${id}/dismiss`),
};

// Role APIs
export const roleAPI = {
  list: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (roleId, data) => api.put(`/roles/${roleId}`, data),
};

// Profile APIs (new permission system)
export const profileAPI = {
  list: () => api.get('/profiles'),
  get: (id) => api.get(`/profiles/${id}`),
  create: (data) => api.post('/profiles', data),
  update: (id, data) => api.put(`/profiles/${id}`, data),
  delete: (id) => api.delete(`/profiles/${id}`),
  clone: (id, data) => api.post(`/profiles/${id}/clone`, data),
};

// Data Role APIs (new permission system)
export const dataRoleAPI = {
  list: () => api.get('/data-roles'),
  create: (data) => api.post('/data-roles', data),
  update: (id, data) => api.put(`/data-roles/${id}`, data),
};

// Permission APIs
export const permissionAPI = {
  getModules: () => api.get('/permissions/modules'),
  getMy: () => api.get('/permissions/my'),
};

// Round 2C (28 Oct 2026, item 8c): legacy categoryAPI/skuAPI removed along with
// SKUsPage.js and the backend's /categories CRUD + /skus endpoints — Zoho Items
// (itemsAPI) is now the single source of truth for SKUs, and categories are a
// read-only Zoho-synced view (zohoAPI.getItemCategories).

// Items API (Zoho Items - Single Source of Truth)
export const itemsAPI = {
  list: (params = {}) => api.get('/items', { params }),
  get: (itemId) => api.get(`/items/${itemId}`),
  getBySku: (sku) => api.get(`/items/by-sku/${sku}`),
  getSummary: () => api.get('/items/summary/stats'),
  getHistory: (sku) => api.get(`/items/${encodeURIComponent(sku)}/purchase-history`),
  assign: (data) => api.post('/items/assign', data),
  checkAssignmentConflicts: (data) => api.post('/items/check-assignment-conflicts', data),
  unassign: (skus) => api.post('/items/unassign', { item_skus: skus }),
  getAssignedTo: (pmId) => api.get(`/items/assigned/${pmId}`),
  getProductManagers: () => api.get('/product-managers'),
  getPriceAnalytics: (days = 14) => api.get('/items-analytics/prices', { params: { days } }),
  debugPriceAnalytics: (sku, days = 14) => api.get(`/items-analytics/debug/${encodeURIComponent(sku)}`, { params: { days } }),
  getRiskAlerts: () => api.get('/items-analytics/risk-alerts'),
  getAgingSummary: () => api.get('/items-analytics/aging-summary'),
  getSkuAging: (sku) => api.get(`/items-analytics/aging/${encodeURIComponent(sku)}`),
  generateRiskNotifications: () => api.post('/items-analytics/generate-risk-notifications'),
};

// Target APIs
export const targetAPI = {
  list: (params = {}) => api.get('/targets', { params }),
  getCurrent: (skuId) => api.get(`/targets/current/${encodeURIComponent(skuId)}`),
  getHistory: (skuId, days = 90) => api.get(`/targets/history/${encodeURIComponent(skuId)}`, { params: { days } }),
  create: (data) => api.post('/targets', data),
  update: (id, data) => api.put(`/targets/${id}`, data),
  delete: (id) => api.delete(`/targets/${id}`),
  downloadSample: () => api.get('/targets/export/sample', { responseType: 'blob' }),
  processImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/targets/import/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getImportStatus: (jobId) => api.get(`/targets/import/status/${jobId}`),
  getRecentImport: () => api.get('/targets/import/recent'),
  applyImportMatch: (data) => api.post('/targets/import/apply-match', data),
};

// Offer APIs
// 21 Oct 2026: Offers & Purchases is retired — only read-only history methods remain.
export const offerAPI = {
  list: (params = {}) => api.get('/offers', { params }),
  get: (id) => api.get(`/offers/${id}`),
};

export const vendorAPI = {
  list: (params = {}) => api.get('/zoho/vendors', { params }),
};

// Purchase APIs — read-only history (Offers & Purchases retired 21 Oct 2026)
export const purchaseAPI = {
  list: (params = {}) => api.get('/purchases', { params }),
};

// Comment APIs
export const commentAPI = {
  list: (entityType, entityId) => api.get('/comments', { params: { entity_type: entityType, entity_id: entityId } }),
  create: (data) => api.post('/comments', data),
};

// Notification APIs
export const notificationAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

// User Search API (for @mentions)
export const userSearchAPI = {
  search: (q) => api.get('/users/search', { params: { q } }),
};

// Excel Import/Export APIs
export const excelAPI = {
  exportTargets: () => api.get('/export/targets', { responseType: 'blob' }),
  importTargets: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/targets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTemplate: (type) => api.get(`/export/template/${type}`, { responseType: 'blob' }),
};

// AI APIs
export const aiAPI = {
  chat: (message, useHighAccuracy = false) => api.post('/ai/chat', { message, use_high_accuracy: useHighAccuracy }),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getEmailLog: (params = {}) => api.get('/settings/email-log', { params }),
  resendEmail: (id) => api.post(`/settings/email-log/${id}/resend`),
  sendTestEmail: () => api.post('/settings/email-test'),
};

// CRM APIs
export const crmAPI = {
  syncUsers: () => api.post('/crm/sync-users'),
  runMatching: () => api.post('/crm/run-matching'),
  getMatchingStatus: () => api.get('/crm/matching-status'),
  getUnmatchedVendors: (limit = 100) => api.get('/crm/unmatched-vendors', { params: { limit } }),
  getUnmatchedCustomers: (limit = 100) => api.get('/crm/unmatched-customers', { params: { limit } }),
  getUsers: () => api.get('/crm/users'),
  getOwnershipOutOfScopeRequests: () => api.get('/crm/ownership-check/out-of-scope-requests'),
  getOwnershipUnownedVendors: () => api.get('/crm/ownership-check/unowned-vendors'),
  getOwnershipStuckApprovers: () => api.get('/crm/ownership-check/stuck-approvers'),
  getOwnershipLatestMatchingRun: () => api.get('/crm/ownership-check/latest-matching-run'),
};

// Alias Catalog APIs
export const aliasAPI = {
  list: (params = {}) => api.get('/alias-catalog', { params }),
  add: (data) => api.post('/alias-catalog', data),
  remove: (alias) => api.delete(`/alias-catalog/${encodeURIComponent(alias)}`),
  importFile: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/alias-catalog/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportUrl: () => `${BACKEND_URL}/api/alias-catalog/export`,
  suggestPartNumbers: () => api.post('/alias-catalog/suggest-part-numbers'),
  getSuggestStatus: (jobId) => api.get(`/alias-catalog/suggest-part-numbers/status/${jobId}`),
  listSuggestions: (status = 'pending') => api.get('/alias-catalog/suggestions', { params: { status } }),
  approveSuggestion: (id) => api.post(`/alias-catalog/suggestions/${id}/approve`),
  rejectSuggestion: (id) => api.post(`/alias-catalog/suggestions/${id}/reject`),
  bulkApproveSuggestions: (ids) => api.post('/alias-catalog/suggestions/bulk-approve', { ids }),
  bulkRejectSuggestions: (ids) => api.post('/alias-catalog/suggestions/bulk-reject', { ids }),
};

// Audit APIs
export const auditAPI = {
  list: (params = {}) => api.get('/audit-logs', { params }),
};

// Reports APIs
export const reportsAPI = {
  categoryPmPerformance: (days = 30) => api.get('/reports/category-pm-performance', { params: { days } }),
};

// Zoho Inventory APIs
export const zohoAPI = {
  getStatus: () => api.get('/zoho/status'),
  getSyncStatus: () => api.get('/zoho/sync-status'),
  sync: (syncType = 'full') => api.post('/zoho/sync', { sync_type: syncType }),
  syncNow: (syncType = 'full') => api.post('/zoho/sync-now', { sync_type: syncType }),
  startPreviewSync: () => api.post('/zoho/preview-sync'),
  previewSyncStatus: () => api.get('/zoho/preview-sync/status'),
  soResyncSince: (sinceDate, dryRun) => api.post('/zoho/so-resync-since', null, { params: { since_date: sinceDate, dry_run: dryRun } }),
  syncPurchasePersons: (limit = 500) => api.post('/zoho/sync-purchase-persons', null, { params: { limit } }),
  getItems: (params = {}) => api.get('/zoho/items', { params }),
  getItemCategories: (params = {}) => api.get('/zoho/items/categories', { params }),
  getUncategorizedItems: () => api.get('/zoho/items/uncategorized'),
  exportUncategorizedItems: () => api.get('/zoho/items/uncategorized/export', { responseType: 'blob' }),
  assignItemCategory: (zohoItemId, categoryName) => api.post(`/zoho/items/${zohoItemId}/assign-category`, { category_name: categoryName }),
  getItem: (itemId) => api.get(`/zoho/items/${itemId}`),
  getVendors: (params = {}) => api.get('/zoho/vendors', { params }),
  getPurchaseOrders: (params = {}) => api.get('/zoho/purchase-orders', { params }),
  getSalesOrders: (params = {}) => api.get('/zoho/sales-orders', { params }),
  getBills: (params = {}) => api.get('/zoho/bills-list', { params }),
  getInvoices: (params = {}) => api.get('/zoho/invoices-list', { params }),
  createPurchaseOrder: (data) => api.post('/zoho/purchase-orders', data),
  getSyncLogs: (params = {}) => api.get('/zoho/sync-logs', { params }),
  getCallDiagnostics: () => api.get('/zoho/call-diagnostics'),
  getFailedSyncDocuments: () => api.get('/zoho/failed-documents'),
  getStockSummary: () => api.get('/zoho/stock-summary'),
  getSchedulerStatus: () => api.get('/zoho/scheduler-status'),
  getLineItemsStatus: () => api.get('/zoho/line-items-status'),
  syncAllLineItems: (data = {}) => api.post('/zoho/sync-all-line-items', data),
  getBulkSyncStatus: () => api.get('/zoho/sync-all-status'),
  stockSyncStatus: () => api.get('/zoho/stock-sync-status'),
  recomputeStock: () => api.post('/zoho/recompute-stock'),
  reconcile: (sampleSize = 50) => api.get('/zoho/reconcile', { params: { sample_size: sampleSize } }),
  fixAllMismatches: () => api.post('/zoho/fix-all-mismatches'),
  debugItem: (sku) => api.get(`/zoho/debug-item/${encodeURIComponent(sku)}`),
  syncWarehouses: () => api.post('/zoho/sync-warehouses'),
};

// Performance Analytics APIs
export const analyticsAPI = {
  getBuyerPerformance: (params = {}) => api.get('/analytics/buyer-performance', { params }),
  getSellerPerformance: (params = {}) => api.get('/analytics/seller-performance', { params }),
  getSummary: () => api.get('/analytics/summary'),
};

// Vendor Payment Approval APIs
export const vendorPaymentAPI = {
  searchPO: (q) => api.get('/vendor-payment/po-search', { params: { q } }),
  getPO: (poNumber) => api.get('/vendor-payment/po', { params: { po_number: poNumber } }),
  fetchPOFromZoho: (poNumber) => api.post('/vendor-payment/po/fetch-from-zoho', null, { params: { po_number: poNumber } }),
  create: (formData) => api.post('/vendor-payment/requests', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (params = {}) => api.get('/vendor-payment/requests', { params }),
  get: (id) => api.get(`/vendor-payment/requests/${id}`),
  check: (id) => api.post(`/vendor-payment/requests/${id}/check`),
  recheck: (id) => api.post(`/vendor-payment/requests/${id}/recheck`),
  updateHeader: (id, data) => api.patch(`/vendor-payment/requests/${id}`, data),
  updateLine: (id, lineId, data) => api.patch(`/vendor-payment/requests/${id}/lines/${lineId}`, data),
  justifyFlag: (id, flagId, justification) => api.post(`/vendor-payment/requests/${id}/flags/${flagId}/justify`, { justification }),
  submit: (id) => api.post(`/vendor-payment/requests/${id}/submit`),
  approve: (id, decision, comment) => api.post(`/vendor-payment/requests/${id}/approve`, { decision, comment }),
  reassign: (id, newApproverUserId, reason) => api.post(`/vendor-payment/requests/${id}/reassign`, { new_approver_user_id: newApproverUserId, reason }),
  pendingApprovalCount: () => api.get('/vendor-payment/pending-approval-count'),
  delete: (id) => api.delete(`/vendor-payment/requests/${id}`),
  cancel: (id, reason) => api.post(`/vendor-payment/requests/${id}/cancel`, { reason }),
  getPriceMatrix: (id) => api.get(`/vendor-payment/requests/${id}/price-matrix`),
  markPaid: (id, data) => api.post(`/vendor-payment/requests/${id}/mark-paid`, data),
  undoPaid: (id, reason) => api.post(`/vendor-payment/requests/${id}/undo-paid`, { reason }),
  exportList: (params = {}) => api.get('/vendor-payment/export', { params, responseType: 'blob' }),
  getFile: (id) => api.get(`/vendor-payment/requests/${id}/file`, { responseType: 'blob' }),
  addSupportingFiles: (id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return api.post(`/vendor-payment/requests/${id}/supporting-files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeSupportingFile: (id, fileId) => api.delete(`/vendor-payment/requests/${id}/supporting-files/${fileId}`),
  getSupportingFile: (id, fileId) => api.get(`/vendor-payment/requests/${id}/supporting-files/${fileId}`, { responseType: 'blob' }),
  rereadLotFile: (id) => api.post(`/vendor-payment/requests/${id}/lot/reread`),
  getLotLines: (id) => api.get(`/vendor-payment/requests/${id}/lot-lines`),
  correctLotLine: (id, lineId, matchedSku) => api.patch(`/vendor-payment/requests/${id}/lot-lines/${lineId}`, { matched_sku: matchedSku }),
};

export const reviewsAPI = {
  list: (params = {}) => api.get('/reviews', { params }),
  get: (id) => api.get(`/reviews/${id}`),
  create: (data) => api.post('/reviews', data),
  update: (id, data) => api.put(`/reviews/${id}`, data),
  submit: (id) => api.post(`/reviews/${id}/submit`),
  approve: (id, data = {}) => api.post(`/reviews/${id}/approve`, data),
  sendBack: (id, comment) => api.post(`/reviews/${id}/send-back`, { comment }),
  employeeHistory: (userId) => api.get(`/reviews/employee/${userId}`),
  due: () => api.get('/reviews/due'),
  employees: () => api.get('/reviews/employees'),
  setReviewLevel: (userId, reviewLevel) => api.put(`/reviews/employees/${userId}/level`, { review_level: reviewLevel }),
  orgChart: () => api.get('/reviews/org-chart'),
  trainers: () => api.get('/reviews/trainers'),
  getNotes: (id) => api.get(`/reviews/${id}/notes`),
  addNote: (id, text) => api.post(`/reviews/${id}/notes`, { text }),
  getReviewTraining: (id) => api.get(`/reviews/${id}/training`),
  myTraining: () => api.get('/reviews/training/mine'),
  getTraining: (id) => api.get(`/reviews/training/${id}`),
  updateTrainingProgress: (id, progress) => api.post(`/reviews/training/${id}/progress`, { progress }),
  addTrainingNote: (id, text) => api.post(`/reviews/training/${id}/notes`, { text }),
};

// Visits & Meetings APIs (Phase 1)
export const meetingsAPI = {
  capabilities: () => api.get('/meetings/me/capabilities'),
  getSettings: () => api.get('/meetings/settings'),
  searchParts: (q) => api.get('/meetings/parts', { params: { q } }),
  searchColleagues: (q, includeInactive) => api.get('/meetings/colleagues', { params: { q, include_inactive: includeInactive || undefined } }),
  searchCompanies: (q) => api.get('/meetings/companies/search', { params: { q } }),
  createProspect: (data) => api.post('/meetings/companies/prospect', data),
  companyTimeline: (type, ref) => api.get('/meetings/companies/timeline', { params: { type, ref } }),
  companyContacts: (type, ref, meetingId) => api.get('/meetings/companies/contacts', { params: { type, ref, meeting_id: meetingId } }),
  updateCompanyProfile: (type, ref, data) => api.put('/meetings/companies/profile', data, { params: { type, ref } }),
  listFieldVisitors: (includeInactive = false) => api.get('/meetings/field-visitors', { params: { include_inactive: includeInactive } }),
  createFieldVisitor: (data) => api.post('/meetings/field-visitors', data),
  updateFieldVisitor: (id, data) => api.put(`/meetings/field-visitors/${id}`, data),
  list: (params = {}) => api.get('/meetings', { params }),
  toTag: (view = 'mine') => api.get('/meetings/to-tag', { params: { view } }),
  tagCompany: (id, data) => api.put(`/meetings/${id}/tag-company`, data),
  tagAttendee: (id, data) => api.put(`/meetings/${id}/tag-attendee`, data),
  get: (id) => api.get(`/meetings/${id}`),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  submit: (id, data) => api.post(`/meetings/${id}/submit`, data),
  sendReport: (id, data) => api.post(`/meetings/${id}/send-report`, data),
  cancel: (id, reason, scope = 'this') => api.post(`/meetings/${id}/cancel`, { reason, scope }),
  reschedule: (id, data) => api.post(`/meetings/${id}/reschedule`, data),
  join: (id) => api.post(`/meetings/${id}/join`),
  addNote: (id, text) => api.post(`/meetings/${id}/notes`, { text }),
  addAction: (id, data) => api.post(`/meetings/${id}/actions`, data),
  review: (id) => api.post(`/meetings/${id}/review`),
  sendBack: (id, comment) => api.post(`/meetings/${id}/send-back`, { comment }),
  uploadFile: (id, kind, file) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    return api.post(`/meetings/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteFile: (id, fileId) => api.delete(`/meetings/${id}/files/${fileId}`),
  sendFileToBidEngine: (id, fileId) => api.post(`/meetings/${id}/files/${fileId}/send-to-bid-engine`),
  fileUrl: (fileId) => `${API_BASE}/meetings/files/${fileId}`,
  listActions: (params = {}) => api.get('/meetings/actions', { params }),
  closeAction: (actionId, data) => api.put(`/meetings/actions/${actionId}`, data),
  editDraftAction: (meetingId, actionId, data) => api.put(`/meetings/${meetingId}/actions/${actionId}/edit`, data),
  deleteDraftAction: (meetingId, actionId) => api.delete(`/meetings/${meetingId}/actions/${actionId}`),
  // Phase 2: Reports
  reportPeople: (params) => api.get('/meetings/reports/people', { params }),
  reportCoverage: (params) => api.get('/meetings/reports/coverage', { params }),
  reportRelationship: (params) => api.get('/meetings/reports/relationship', { params }),
  reportActions: (params) => api.get('/meetings/reports/actions', { params }),
  reportResults: (params) => api.get('/meetings/reports/results', { params }),
  reportMarketFeedback: (params) => api.get('/meetings/reports/market-feedback', { params }),
  reportReviewDiscipline: (params) => api.get('/meetings/reports/review-discipline', { params }),
  reportVisitsByPerson: (params) => api.get('/meetings/reports/visits-by-person', { params }),
  // Phase 2: Admin
  adminGetSettings: () => api.get('/meetings/admin/settings'),
  adminUpdateSettings: (data) => api.put('/meetings/admin/settings', data),
  adminWeeklyDigestCandidates: () => api.get('/meetings/admin/weekly-digest-candidates'),
  adminListQuestions: () => api.get('/meetings/admin/questions'),
  adminCreateQuestion: (data) => api.post('/meetings/admin/questions', data),
  adminUpdateQuestion: (id, data) => api.put(`/meetings/admin/questions/${id}`, data),
  adminDeleteQuestion: (id) => api.delete(`/meetings/admin/questions/${id}`),
  adminListProspects: (status = 'UNMATCHED') => api.get('/meetings/admin/prospects', { params: { status } }),
  adminMatchProspect: (id, data) => api.post(`/meetings/admin/prospects/${id}/match`, data),
  adminKeepAsProspect: (id) => api.post(`/meetings/admin/prospects/${id}/keep-as-prospect`),
  adminReassign: (data) => api.post('/meetings/admin/reassign', data),
  adminListProductCategories: () => api.get('/meetings/admin/product-categories'),
  adminCreateProductCategory: (data) => api.post('/meetings/admin/product-categories', data),
  adminUpdateProductCategory: (code, data) => api.put(`/meetings/admin/product-categories/${code}`, data),
};


export const offerStoreAPI = {
  list: (params = {}) => api.get('/offer-store/offers', { params }),
  get: (id) => api.get(`/offer-store/offers/${id}`),
  create: (data) => api.post('/offer-store/offers', data),
  update: (id, data) => api.patch(`/offer-store/offers/${id}`, data),
  remove: (id, reason) => api.delete(`/offer-store/offers/${id}`, { data: { reason } }),
  getVendors: () => api.get('/offer-store/vendors'),
  uploadPreview: (file, vendorZohoId, vendorName, offerDate) => {
    const form = new FormData();
    form.append('file', file);
    if (vendorZohoId) form.append('vendor_zoho_id', vendorZohoId);
    if (vendorName) form.append('vendor_name', vendorName);
    if (offerDate) form.append('offer_date', offerDate);
    return api.post('/offer-store/upload/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadCommit: (previewId) => api.post('/offer-store/upload/commit', { preview_id: previewId }),
  exportUrl: (params = {}) => `${API_BASE}/offer-store/export?${new URLSearchParams(params).toString()}`,
  export: (params = {}) => api.get('/offer-store/export', { params, responseType: 'blob' }),
  backfillBidEngine: () => api.post('/offer-store/backfill-bid-engine'),
};

export const leadsAPI = {
  getCapabilities: () => api.get('/leads/me/capabilities'),
  updatePrefs: (tz) => api.put('/leads/me/prefs', { tz }),
  getSettings: () => api.get('/leads/settings'),
  updateSettings: (data) => api.put('/leads/settings', data),
  list: (params = {}) => api.get('/leads', { params }),
  myDay: () => api.get('/leads/my-day'),
  get: (id) => api.get(`/leads/${id}`),
  getActivities: (id) => api.get(`/leads/${id}/activities`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  logActivity: (id, data) => api.post(`/leads/${id}/activities`, data),
  close: (id, data) => api.post(`/leads/${id}/close`, data),
  reopen: (id, data) => api.post(`/leads/${id}/reopen`, data),
  setStatus: (id, data) => api.post(`/leads/${id}/set-status`, data),
  checkDuplicates: (id, data) => api.post(`/leads/${id}/check-duplicates`, data),
  assign: (data) => api.post('/leads/assign', data),
  reassignAll: (data) => api.post('/leads/admin/reassign-all', data),
  merge: (data) => api.post('/leads/admin/merge', data),
  getTemplate: () => api.get('/leads/upload/template'),
  uploadPreview: (file, params) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, v); });
    return api.post('/leads/upload/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadConfirm: (previewId, keepFlaggedRows) => api.post(`/leads/upload/${previewId}/confirm`, { keep_flagged_rows: keepFlaggedRows }),
  listBatches: () => api.get('/leads/batches'),
  getBatch: (id) => api.get(`/leads/batches/${id}`),
  searchAssignees: (q) => api.get('/leads/assignees', { params: { q } }),
  getFile: (id) => api.get(`/leads/files/${id}`),
  getSettingsAdmin: () => api.get('/leads/settings/admin'),
  updateSettingsAdmin: (data) => api.put('/leads/settings/admin', data),
  reportsSummary: (scope) => api.get('/leads/reports/summary', { params: { scope } }),
  reportsTeam: (scope) => api.get('/leads/reports/team', { params: { scope } }),
  reportsActivity: (scope, days) => api.get('/leads/reports/activity', { params: { scope, days } }),
  reportsFunnel: (scope) => api.get('/leads/reports/funnel', { params: { scope } }),
  reportsOverdue: (scope) => api.get('/leads/reports/overdue', { params: { scope } }),
  reportsConversions: (scope) => api.get('/leads/reports/conversions', { params: { scope } }),
  reportsSources: () => api.get('/leads/reports/sources'),
  reportsDataHealth: () => api.get('/leads/reports/data-health'),
  feedPullNow: () => api.post('/leads/feed/pull-now'),
  listFeed: (status) => api.get('/leads/feed', { params: status ? { status } : {} }),
  zohoImportStart: (leadsFile, notesFile, dryRun) => {
    const form = new FormData();
    form.append('leads_csv', leadsFile);
    if (notesFile) form.append('notes_csv', notesFile);
    form.append('dry_run', dryRun ? 'true' : 'false');
    return api.post('/leads/admin/zoho-import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listZohoImportJobs: () => api.get('/leads/admin/zoho-import'),
  getZohoImportJob: (id) => api.get(`/leads/admin/zoho-import/${id}`),
  convert: (id, data) => api.post(`/leads/${id}/convert`, data),
  convertSearch: (id, targetType, q) => api.get(`/leads/${id}/convert/search`, { params: { target_type: targetType, q } }),
};

export const sourcingAPI = {
  getCapabilities: () => api.get('/sourcing/me/capabilities'),
  getSettings: () => api.get('/sourcing/settings'),
  getAdminSettings: () => api.get('/sourcing/admin/settings'),
  updateSettings: (data) => api.put('/sourcing/admin/settings', data),
  listParts: (params = {}) => api.get('/sourcing/parts', { params }),
  refreshParts: () => api.post('/sourcing/parts/refresh'),
  getPart: (skuId) => api.get(`/sourcing/parts/${encodeURIComponent(skuId)}`),
  updatePart: (skuId, data) => api.put(`/sourcing/parts/${encodeURIComponent(skuId)}`, data),
  uploadPartsPreview: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/sourcing/parts/upload/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  confirmPartsUpload: (previewId) => api.post(`/sourcing/parts/upload/${previewId}/confirm`),
  searchStored: (part) => api.get('/sourcing/search', { params: { part } }),
  searchLive: (part) => api.post('/sourcing/search', null, { params: { part } }),
  scanNow: () => api.post('/sourcing/scan'),
  listScans: () => api.get('/sourcing/scans'),
  listVendors: (q = '') => api.get('/sourcing/vendors', { params: { q } }),
  vendorStock: (companyId) => api.get(`/sourcing/vendors/${companyId}/stock`),
  pullVendorStock: (companyId) => api.post(`/sourcing/vendors/${companyId}/pull`),
  listCompanies: (q = '') => api.get('/sourcing/admin/companies', { params: { q } }),
  updateCompany: (id, data) => api.put(`/sourcing/admin/companies/${id}`, data),
  companySuggestions: (id) => api.get(`/sourcing/admin/companies/${id}/suggestions`),
  listVariants: (skuId = '', status = 'PENDING') => api.get('/sourcing/variants', { params: { sku_id: skuId, status } }),
  decideVariant: (id, decision) => api.put(`/sourcing/variants/${id}`, { decision }),
  listDrafts: (view = 'mine', status = '', tier = '') => api.get('/sourcing/drafts', { params: { view, status, tier } }),
  getDraft: (id) => api.get(`/sourcing/drafts/${id}`),
  reassignDraft: (id, assigneeId) => api.put(`/sourcing/drafts/${id}/reassign`, { assignee_id: assigneeId }),
  sendDraft: (id, data) => api.post(`/sourcing/drafts/${id}/send`, data),
  sendPreview: (id) => api.get(`/sourcing/drafts/${id}/send-preview`),
  listRfqs: (params = {}) => api.get('/sourcing/rfqs', { params }),
  getRfq: (id) => api.get(`/sourcing/rfqs/${id}`),
  setRfqOutcome: (id, data) => api.put(`/sourcing/rfqs/${id}/outcome`, data),
  setLineOutcome: (rfqId, lineId, data) => api.put(`/sourcing/rfqs/${rfqId}/lines/${lineId}/outcome`, data),
  setBulkLineOutcome: (rfqId, data) => api.put(`/sourcing/rfqs/${rfqId}/lines/outcome-bulk`, data),
  sendRfqToBidEngine: (id) => api.post(`/sourcing/rfqs/${id}/send-to-bid-engine`),
  sendTestMessage: () => api.post('/sourcing/test-message'),
  hotList: (params = {}) => api.get('/sourcing/hot', { params }),
  hotTiers: (skuIds = []) => api.get('/sourcing/hot', { params: { sku_ids: skuIds.join(',') } }),
  hotDetail: (skuId) => api.get(`/sourcing/hot/${skuId}`),
  rebuildHot: () => api.post('/sourcing/hot/rebuild'),
  adminUsage: () => api.get('/sourcing/admin/usage'),
  adminInbox: () => api.get('/sourcing/admin/inbox'),
  findVstlOwner: () => api.get('/sourcing/admin/find-vstl-owner'),
  recipientCandidates: () => api.get('/sourcing/admin/recipient-candidates'),
  zohoVendorSearch: (q) => api.get('/sourcing/admin/zoho-vendors', { params: { q } }),
  scanReport: () => api.get('/sourcing/admin/scan-report'),
  bulkMarkNotSearchable: (skuIds, reason) => api.post('/sourcing/parts/bulk-mark-not-searchable', { sku_ids: skuIds, reason }),
  supplyDemandHistory: (skuId) => api.get(`/sourcing/part/${encodeURIComponent(skuId)}/history/supply-demand`),
  forceRescanPreview: (mode, partIds = []) => api.get('/sourcing/scan/force/preview', { params: { mode, part_ids: partIds.join(',') } }),
  forceRescan: (mode, partIds = []) => api.post('/sourcing/scan/force', { mode, part_ids: partIds.length ? partIds : undefined }),
};


export default api;
